import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Check, ChevronRight, Play, Smartphone, Code, Upload, Plus, Layout } from 'lucide-react';

const STEPS = [
    {
        id: 'create_flow',
        title: 'Create Flow',
        description: 'Start with a blank flow or a template. This is where users go when they open your app.',
        icon: Plus,
        cta: 'Create Flow'
    },
    {
        id: 'add_screens',
        title: 'Add Screens',
        description: 'Add screens, reorder them, and customize the experience on the fly.',
        icon: Layout, // Assuming Layout is imported or available
        cta: 'Next'
    },
    {
        id: 'preview',
        title: 'Preview on Device',
        description: 'Scan the QR code to view your onboarding in real time.',
        icon: Smartphone,
        cta: 'Next'
    },
    {
        id: 'install_sdk',
        title: 'Install SDK',
        description: 'Add one snippet to your app to load your onboarding remotely.',
        icon: Code,
        cta: 'Next' // Or 'Check Installation' if we can detect it
    },
    {
        id: 'publish',
        title: 'Publish',
        description: 'Push changes to your app instantly. No App Store update required.',
        icon: Upload,
        cta: 'Publish'
    }
];

const WalkthroughModal = ({ open, onOpenChange, onComplete, onAction }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);

    const step = STEPS[currentStep];
    const progress = ((currentStep + 1) / STEPS.length) * 100;

    const handleNext = () => {
        if (currentStep < STEPS.length - 1) {
            setCompletedSteps([...completedSteps, step.id]);
            setCurrentStep(currentStep + 1);
        } else {
            handleComplete();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = () => {
        setCompletedSteps([...completedSteps, step.id]);
        onComplete();
    };

    const handleActionClick = () => {
        if (onAction) {
            onAction(step.id);
        }
        // For some steps, the action itself might advance the flow (e.g. Create Flow)
        // For others (like Preview), user might click Next manually
        if (step.id !== 'create_flow' && step.id !== 'publish') {
            handleNext();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
                <div className="bg-muted/30 p-6 border-b">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs">
                                {currentStep + 1}
                            </span>
                            Step {currentStep + 1} of {STEPS.length}
                        </div>
                        {/* Simple Progress Bar */}
                        <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                                className="h-full bg-primary transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                    <DialogHeader>
                        <DialogTitle className="text-2xl flex items-center gap-3">
                            <div className="p-2 bg-background rounded-lg border shadow-sm">
                                <step.icon className="h-6 w-6 text-primary" />
                            </div>
                            {step.title}
                        </DialogTitle>
                    </DialogHeader>
                </div>

                <div className="p-6 space-y-6">
                    <DialogDescription className="text-base text-foreground">
                        {step.description}
                    </DialogDescription>

                    {/* Step Indicators */}
                    <div className="flex justify-between px-2">
                        {STEPS.map((s, idx) => (
                            <div key={s.id} className="flex flex-col items-center gap-2">
                                <div
                                    className={`w-2 h-2 rounded-full transition-colors ${idx === currentStep ? 'bg-primary scale-125' :
                                        idx < currentStep ? 'bg-primary/50' : 'bg-muted'
                                        }`}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <DialogFooter className="p-6 bg-muted/10 border-t flex justify-between items-center">
                    <Button
                        variant="ghost"
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        className="text-muted-foreground"
                    >
                        Back
                    </Button>
                    <div className="flex gap-2">
                        {step.id !== 'create_flow' && step.id !== 'publish' && (
                            <Button variant="outline" onClick={handleNext}>
                                Skip for now
                            </Button>
                        )}
                        <Button onClick={handleActionClick} className="gap-2">
                            {step.cta}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default WalkthroughModal;
